import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'motion/react';
import { useMemo, useState } from 'react';
import { Icon } from '../components/Icon.tsx';
import { EASE } from '../components/motion.tsx';
import { Avatar, Button, Empty, PageHead, Segmented, Skeleton, StatusChip } from '../components/ui.tsx';
import { get, photoUrl, post, qs } from '../lib/api.ts';
import { addDays, date, time, todayIn } from '../lib/format.ts';
import { useLookups, useMe } from '../lib/session.ts';
import { useToast } from '../components/ui.tsx';
import { AttendanceReport, AttendanceTabs, type attendanceSearch } from './AttendanceReport.tsx';

interface BoardRow {
  employee: { id: string; name: string; employeeNo: string | null; jobPosition: string | null };
  schedule: { kind: string; shift: string | null; start: string | null; end: string | null } | null;
  state: string;
  clockInAt: string | null;
  clockOutAt: string | null;
  lateMinutes: number;
  source: string | null;
  flags: string[];
}
interface Board {
  date: string;
  timeZone: string;
  tally: Record<string, number>;
  late: number;
  rows: BoardRow[];
}

const SCALE_START = 5 * 60;
const SCALE_END = 24 * 60;
const pct = (min: number) => `${Math.max(0, Math.min(100, ((min - SCALE_START) / (SCALE_END - SCALE_START)) * 100))}%`;
const hm = (s: string) => Number(s.slice(0, 2)) * 60 + Number(s.slice(3, 5));
const localMin = (ts: string, tz: string) => hm(time(ts, tz));

const SOURCE_LABEL: Record<string, string> = { fingerspot: 'Fingerspot', face_personal: 'Face · web', face_kiosk: 'Face · kiosk', web: 'Web', mobile: 'Mobile', correction: 'Correction', qr_kiosk: 'QR kiosk' };

export function Attendance() {
  const { view } = useSearch({ strict: false }) as ReturnType<typeof attendanceSearch>;
  return view === 'report' ? <AttendanceReport /> : <LiveBoard />;
}

function LiveBoard() {
  const me = useMe().data!;
  const lookups = useLookups();
  const toast = useToast();
  const navigate = useNavigate();
  const [officeId, setOfficeId] = useState(me.employee?.officeId ?? '');
  const tz = lookups.data?.offices.find((o) => o.id === officeId)?.timeZone ?? me.employee?.timeZone ?? 'Asia/Makassar';
  const [day, setDay] = useState(() => todayIn(tz));
  const [filter, setFilter] = useState<'all' | 'in' | 'not_in' | 'late' | 'done'>('all');
  const q = useQuery({ queryKey: ['board', officeId, day], queryFn: () => get<Board>(`/api/attendance/board${qs({ officeId, date: day })}`), refetchInterval: day === todayIn(tz) ? 30_000 : false });

  const rows = useMemo(() => {
    const all = q.data?.rows ?? [];
    if (filter === 'all') return all;
    if (filter === 'late') return all.filter((r) => r.lateMinutes > 0);
    return all.filter((r) => r.state === filter);
  }, [q.data, filter]);
  const isToday = day === todayIn(tz);
  const nowMin = hm(time(Date.now(), tz));

  return (
    <div>
      <PageHead
        eyebrow={`Live board · ${date(day, { weekday: true })}`}
        title={
          isToday ? (
            <>
              Who’s <em>here</em> today.
            </>
          ) : (
            <>
              {date(day, { weekday: true, year: false })}, <em>in review.</em>
            </>
          )
        }
        lede="Every punch is kept as it happened. Timesheets are derived from them, so corrections never overwrite the original record."
        aside={
          <>
            <div className="row" style={{ ['--gap' as string]: '4px' }}>
              <Button variant="ghost" size="sm" iconOnly icon="chevronLeft" aria-label="Previous day" onClick={() => setDay(addDays(day, -1))} />
              <input type="date" className="input" style={{ width: 160, height: 32 }} value={day} max={todayIn(tz)} onChange={(e) => setDay(e.target.value)} aria-label="Date" />
              <Button variant="ghost" size="sm" iconOnly icon="chevronRight" aria-label="Next day" disabled={isToday} onClick={() => setDay(addDays(day, 1))} />
            </div>
            {me.access.attendanceAdmin ? (
              <Button
                size="sm"
                icon="refresh"
                onClick={async () => {
                  const r = await post<{ written: number }>('/api/attendance/rebuild', { officeId: officeId || undefined, from: day, to: day });
                  toast({ title: 'Re-derived from punches', body: `${r.written} timesheets rebuilt for ${date(day)}.` });
                  void q.refetch();
                }}
              >
                Rebuild day
              </Button>
            ) : null}
          </>
        }
      />
      <AttendanceTabs value="board" />
      <div className="toolbar">
        <select className="select" style={{ width: 'auto' }} value={officeId} onChange={(e) => setOfficeId(e.target.value)} aria-label="Office">
          <option value="">All offices</option>
          {lookups.data?.offices.map((o) => (
            <option key={o.id} value={o.id}>
              {o.name}
            </option>
          ))}
        </select>
        <Segmented
          label="Filter"
          idPrefix="board"
          value={filter}
          onChange={setFilter}
          options={[
            { id: 'all', label: `All ${q.data?.rows.length ?? ''}` },
            { id: 'in', label: `In ${q.data?.tally.in ?? 0}` },
            { id: 'late', label: `Late ${q.data?.late ?? 0}` },
            { id: 'not_in', label: `Not in ${q.data?.tally.not_in ?? 0}` },
            { id: 'done', label: `Done ${q.data?.tally.done ?? 0}` },
          ]}
        />
        {isToday ? (
          <span className="chip chip--accent chip--live" style={{ marginLeft: 'auto' }}>
            Live · {time(Date.now(), tz)}
          </span>
        ) : null}
      </div>

      <div className="panel" style={{ padding: '4px 24px 8px' }}>
        <div className="board-row eyebrow" style={{ borderBottom: '1px solid var(--line-2)' }}>
          <span>Person</span>
          <span>State</span>
          <span>
            05:00 <span style={{ float: 'right' }}>24:00</span>
          </span>
          <span>In / out</span>
          <span style={{ textAlign: 'right' }}>Source</span>
        </div>
        {q.isLoading ? (
          Array.from({ length: 8 }, (_, i) => (
            <div key={i} className="board-row">
              <Skeleton h={28} />
              <Skeleton h={20} w={70} />
              <Skeleton h={8} />
              <Skeleton h={14} w={100} />
              <Skeleton h={14} w={70} />
            </div>
          ))
        ) : !rows.length ? (
          <Empty title="Nobody here.">No one matches this filter for {date(day)}.</Empty>
        ) : (
          <AnimatePresence initial={false}>
            {rows.map((r, i) => {
              const s = r.schedule?.start && r.schedule.end ? { a: hm(r.schedule.start), b: hm(r.schedule.end) < hm(r.schedule.start) ? SCALE_END : hm(r.schedule.end) } : null;
              const inMin = r.clockInAt ? localMin(r.clockInAt, q.data!.timeZone) : null;
              const outMin = r.clockOutAt ? localMin(r.clockOutAt, q.data!.timeZone) : isToday && inMin !== null ? nowMin : null;
              return (
                <motion.div key={r.employee.id} className="board-row" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.4, ease: EASE, delay: Math.min(i, 20) * 0.015 }} onClick={() => navigate({ to: '/attendance/$employeeId', params: { employeeId: r.employee.id } })} style={{ cursor: 'pointer' }}>
                  <div className="row" style={{ minWidth: 0 }}>
                    <Avatar name={r.employee.name} size={32} photo={photoUrl(r.employee.id)} status={r.state === 'in' ? (r.lateMinutes ? 'late' : 'in') : r.state === 'done' ? 'out' : null} />
                    <div style={{ minWidth: 0 }}>
                      <Link to="/attendance/$employeeId" params={{ employeeId: r.employee.id }} className="link" onClick={(e) => e.stopPropagation()}>
                        {r.employee.name}
                      </Link>
                      <div className="faint" style={{ fontSize: 'var(--step--2)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {r.employee.jobPosition} {r.schedule?.shift ? <span className="mono">· {r.schedule.shift}</span> : null}
                      </div>
                    </div>
                  </div>
                  <div>
                    <StatusChip status={r.state} live={r.state === 'in' && isToday} />
                  </div>
                  <div className="timeline" aria-label={s ? `Shift ${r.schedule?.start}–${r.schedule?.end}` : 'No shift'}>
                    <div className="timeline__track" />
                    {s ? <div className="timeline__shift" style={{ left: pct(s.a), width: `calc(${pct(s.b)} - ${pct(s.a)})` }} /> : null}
                    {inMin !== null && outMin !== null ? (
                      <motion.div className="timeline__worked" style={{ left: pct(inMin), background: r.lateMinutes ? 'var(--clay)' : 'var(--sage)' }} initial={{ width: 0 }} animate={{ width: `calc(${pct(Math.max(outMin, inMin))} - ${pct(inMin)})` }} transition={{ duration: 0.9, ease: EASE, delay: 0.1 }} />
                    ) : null}
                    {isToday ? <div className="timeline__now" style={{ left: pct(nowMin) }} /> : null}
                  </div>
                  <div className="mono num" style={{ fontSize: 12 }}>
                    {r.clockInAt ? time(r.clockInAt, q.data!.timeZone) : '—'} <span className="faint">→</span> {r.clockOutAt ? time(r.clockOutAt, q.data!.timeZone) : '—'}
                    {r.lateMinutes ? <span style={{ color: 'var(--clay)', marginLeft: 8 }}>+{r.lateMinutes}m</span> : null}
                  </div>
                  <div className="faint" style={{ textAlign: 'right', fontSize: 'var(--step--2)' }}>
                    {r.source ? (
                      <span className="row" style={{ justifyContent: 'flex-end', ['--gap' as string]: '6px' }}>
                        <Icon name={r.source.startsWith('face') ? 'face' : r.source === 'correction' ? 'formula' : 'fingerprint'} width={14} height={14} />
                        {SOURCE_LABEL[r.source] ?? r.source}
                      </span>
                    ) : (
                      '—'
                    )}
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        )}
      </div>
    </div>
  );
}
